import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { usePollUntilPendingBrandPlanSchedules } from 'hooks/queries/schedules/usePollUntilPendingBrandPlanSchedules'
import { useMutation, useQueryClient } from 'react-query'
import { reportError } from 'utils/error'
import { scheduleKeys } from '../schedules/scheduleKeys'

export type ContentScope = 'current_batch' | 'user_selected_content' | 'upcoming_week_content'

export interface RegenerateBody {
  topic?: string
  file_ids?: string[]
  initial_media_file_ids?: string[]
  content_scope?: ContentScope
}

const regenerateContent = async ({
  workspaceId,
  brandPlanId,
  body,
}: {
  workspaceId: number
  brandPlanId: number
  body: RegenerateBody
}) => {
  const res = await sendToApi(`/w/${workspaceId}/brand_plans/${brandPlanId}/regenerate_content`, {
    method: 'POST',
    body,
  })
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res.data
}

type Data = {
  success: boolean
  regenerated_count: number
  message: string
  content_ids?: number[]
  schedule_id?: number
}

interface UseRegenerateBrandPlanContentOptions {
  onError?: (error: Error) => void
  onSuccess?: (data: Data) => void
  onSettled?: () => void
}

export const useRegenerateBrandPlanContent = (options?: UseRegenerateBrandPlanContentOptions) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const pollUntilPendingSchedules = usePollUntilPendingBrandPlanSchedules({})

  return useMutation<Data, Error, { brandPlanId: number; body: RegenerateBody }>(
    ({ brandPlanId, body }) => regenerateContent({ workspaceId, brandPlanId, body }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(scheduleKeys.pending(workspaceId))
        pollUntilPendingSchedules()
        options?.onSuccess?.(data)
      },
      onError: (error) => {
        reportError(error)
        options?.onError?.(error)
      },
      onSettled: options?.onSettled,
    },
  )
}
